import { useCallback, useEffect, useMemo } from "react";

import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import type { LegCardIdClassAndFare } from "@app/constants/seat-selection-first";
import { OUTBOUND_LCF, RETURN_LCF } from "@app/constants/seat-selection-first";
import { useAppContext, useAppDispatch } from "@app/helpers/hooks";
import { lazySentryCaptureException } from "@app/helpers/sentry-capture";
import { buildLegCardIdClassAndFareHash } from "@app/helpers/udm/hashes";
import {
  addToCart,
  closeDrawer,
  trackSelectedTrip,
  updateSearch
} from "@app/modules/results/actions";
import { useSeatSelectionFirst } from "@app/modules/seat-selection-first/hooks/use-seat-selection-first";
import type { SegmentSelectedSeats } from "@app/modules/seat-selection-first/store";
import { closeAllDrawers } from "@app/store/state/drawers";
import type { TripOption } from "@app/types/results-types";
import { open } from "@app/utils/window";

import type { Drawer, Page } from "../constants/pages";
import type { GoToOptions } from "../helpers/views/results";
import type express from "express";

import { shouldSkipAsyncCart } from "./helpers";
import { decodeSearchParams } from "./navigation/decode-search-params";
import { DRAWERS, PAGES } from "../constants/pages";
import { pageToRender } from "../helpers/page-to-render";
import {
  AsyncCartView,
  ResultsView,
  SeatSelectionView,
  TicketOptionsView
} from "../helpers/views";
import {
  CART_ID_KEY,
  JOURNEY_ID_KEY,
  OPEN_DRAWER_KEY,
  SEGMENT_ID_KEY,
  SELECTED_LEG_CARD_ID_KEY,
  SELECTED_SEATS_KEY,
  TRIP_ID_KEY,
  VIEW_KEY
} from "../helpers/views/constants";
import { getSearch } from "../selectors/state-accessors";
import { changePage } from "../state/page";

export type {
  SelectedSeatsBySegment,
  SelectedSeatsQueryParam
} from "./navigation/decode-search-params";

export interface NavigationState {
  error?: string;
  leg_card_id?: string | null;
}

// useNavigationState has been extracted outside of useNavigation
// as useNavigation is called from all the departure card it created as many listeners
// but we just need one at the root level to track page changes
export const useNavigationState = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    history.listen(({ search }, action) => {
      // Back button click
      if (action === "POP") {
        window.dispatchEvent(new Event("back"));
      }
      if (action) {
        // Who the heck is using this
        dispatch(changePage(getPageClient(search)));
      }
    });
  }, [dispatch, history]);
};

export const useNavigation = () => {
  const history = useHistory<NavigationState>();
  const state_search = useSelector(getSearch);
  const { device, features, urlBuilder } = useAppContext();
  const { is_seat_selection_first_enabled } = useSeatSelectionFirst();
  const dispatch = useAppDispatch();

  const search_params = useMemo(() => {
    let search = "";
    try {
      search = history.location.search;
    } catch (e) {
      lazySentryCaptureException(e);
    }
    return new URLSearchParams(search);
  }, [history.location.search]);
  const history_state = history.location.state;

  const {
    leg_card_id,
    outbound_leg_card_id_class_and_fare,
    return_leg_card_id_class_and_fare,
    trip_id,
    cart_id,
    segment_id,
    selected_seats,
    is_roundtrip,
    view,
    drawer,
    utm_campaign,
    journey_id
  } = decodeSearchParams(search_params, history_state);

  const is_roundtrip_outbound_view = is_roundtrip && !cart_id;
  // TODO-SSF: has_seat_selection needs to be addressed. Here or in the switch where page is rendered because of the flow
  const page_to_render = pageToRender(view as Page);
  const drawer_to_render = drawer;

  const goBack = (error?: string) => {
    const is_in_oneway_seat_selection_first =
      outbound_leg_card_id_class_and_fare && !return_leg_card_id_class_and_fare;

    const is_in_roundtrip_seat_selection_first =
      outbound_leg_card_id_class_and_fare && return_leg_card_id_class_and_fare;

    if (is_in_oneway_seat_selection_first) {
      search_params.delete(OUTBOUND_LCF);
      dispatch(updateSearch({ outbound_leg_card_id_class_and_fare: null }));
    }
    // Is Roundtrip Seat Selection First
    else if (is_in_roundtrip_seat_selection_first) {
      search_params.delete(RETURN_LCF);
      dispatch(updateSearch({ return_leg_card_id_class_and_fare: null }));
    }

    if (
      is_in_oneway_seat_selection_first ||
      is_in_roundtrip_seat_selection_first
    ) {
      search_params.delete(SELECTED_SEATS_KEY);
    }

    if (error) {
      // On error, we redirect to outbound results anyway
      restartSearch({ leg_card_id, error });
      return;
    }

    redirectToResults({ leg_card_id, error });
  };

  const onDrawerClose = useCallback(() => {
    history.goBack();
  }, [history]);

  const onDrawerError = (error?: string) => {
    if (history.location.state?.error === error) {
      return;
    }

    history.replace({
      search: history.location.search,
      state: { error }
    });
  };

  const redirectToCart = (trip_id: string, is_external: boolean = false) => {
    if (!trip_id) {
      // TODO-ASYNC-CART: better error handling in case of no trip_id
      throw new Error("Trip ID is required to redirect to async cart");
    }

    const should_skip_async_cart = shouldSkipAsyncCart(
      is_roundtrip,
      cart_id,
      is_seat_selection_first_enabled,
      features
    );

    if (is_external || should_skip_async_cart) {
      if (is_external && !is_roundtrip) {
        return open(
          urlBuilder.deeplink(state_search, { outbound_id: trip_id })
        );
      }

      return dispatch(addToCart(trip_id));
    }

    if (device.is_mobile) {
      dispatch(closeAllDrawers());
    }

    AsyncCartView.goTo(search_params, device.is_mobile, trip_id, history);
  };

  interface RedirectToReturnResultsParams {
    outbound_leg_card_id_class_and_fare: LegCardIdClassAndFare;
    trip_id: string;
    is_external: boolean;
  }
  const redirectToReturnResults = (params: RedirectToReturnResultsParams) => {
    search_params.set(OUTBOUND_LCF, params.outbound_leg_card_id_class_and_fare);
    dispatch(
      updateSearch({
        outbound_leg_card_id_class_and_fare:
          params.outbound_leg_card_id_class_and_fare
      })
    );
    redirectToCart(params.trip_id, params.is_external);
  };

  const redirectToSeatSelection = (
    leg_card_id_class_and_fare: string,
    segment_id: string
  ) => {
    SeatSelectionView.goTo(
      search_params,
      leg_card_id_class_and_fare,
      history,
      device,
      is_roundtrip,
      segment_id
    );
  };

  const redirectToTicketOptions = (leg_card_id: string) => {
    TicketOptionsView.goTo(
      search_params,
      leg_card_id,
      history,
      device.is_mobile
    );
  };

  const redirectToResults = (
    state: NavigationState = {},
    options: GoToOptions = {}
  ) => {
    search_params.delete(OPEN_DRAWER_KEY);
    search_params.delete(SEGMENT_ID_KEY);
    search_params.delete(SELECTED_LEG_CARD_ID_KEY);
    search_params.delete(TRIP_ID_KEY);

    ResultsView.goTo(search_params, history, state, options);
  };

  const restartSearch = (state: NavigationState = {}) => {
    ResultsView.goTo(search_params, history, state, {
      force_refresh: true,
      restart_spa_search: true
    });
  };

  const addCartId = (created_cart_id: string) => {
    if (!cart_id || cart_id !== created_cart_id) {
      search_params.set(CART_ID_KEY, created_cart_id);
      history.replace({
        search: search_params.toString()
      });
    }
  };

  const removeCartId = () => {
    search_params.delete(CART_ID_KEY);
    history.replace({ search: search_params.toString() });
  };

  // The selectTrip works from the TicketOptios to the Seat Selection. For the seat selection page we use this function as we always use the same leg_card_id_class_and_fare already set on the Query Params
  const selectSeats = (
    selected_trip: TripOption,
    is_external?: boolean,
    selected_seats: SegmentSelectedSeats[] | number = [],
    segment_id?: string
  ) => {
    const leg_card_id_class_and_fare = return_leg_card_id_class_and_fare
      ? return_leg_card_id_class_and_fare
      : outbound_leg_card_id_class_and_fare || "";

    const has_seat_selection = !!segment_id;

    // We also set the selected seats in the redirectToNextSSFStep function. However, there it is set only when there is no seat selection. Here we cover the Seat Selection case.
    SeatSelectionView.setSelectedSeatsQS(search_params, selected_seats);

    return redirectToNextSSFStep({
      leg_card_id_class_and_fare,
      trip_id: selected_trip?.trip_id,
      is_external: !!is_external,
      has_seat_selection,
      selected_seats,
      segment_id
    });
  };

  const selectTrip = (
    selected_trip: TripOption,
    is_external?: boolean,
    has_seat_selection: boolean | undefined = true,
    selected_seats: SegmentSelectedSeats[] | number = [],
    segment_id?: string
  ) => {
    dispatch(trackSelectedTrip(selected_trip?.trip_id));

    if (is_seat_selection_first_enabled) {
      // Before reach the Seat selection page we generate the leg_card_id_class_and_fare using the leg_card_id of the selected data, in the outbound and/or return ticket options page. After we reach the Seat Selection se use the `selectSeats` function
      const leg_card_id_class_and_fare = buildLegCardIdClassAndFareHash({
        leg_card_id: leg_card_id!,
        class_xid: selected_trip?.class_xid,
        fare_id: selected_trip?.fare_id
      });

      return redirectToNextSSFStep({
        leg_card_id_class_and_fare,
        trip_id: selected_trip?.trip_id,
        is_external: !!is_external,
        has_seat_selection,
        selected_seats,
        segment_id
      });
    }

    redirectToCart(selected_trip?.trip_id, is_external);
  };

  const redirectToNextSSFStep = ({
    leg_card_id_class_and_fare,
    trip_id,
    is_external,
    has_seat_selection = true,
    selected_seats = [],
    segment_id
  }: {
    leg_card_id_class_and_fare: string;
    trip_id: string;
    is_external?: boolean;
    has_seat_selection: boolean | undefined;
    selected_seats: SegmentSelectedSeats[] | number;
    segment_id?: string;
  }) => {
    if (is_roundtrip_outbound_view) {
      return redirectToReturnResults({
        outbound_leg_card_id_class_and_fare: leg_card_id_class_and_fare,
        trip_id,
        is_external: !!is_external
      });
    }

    if (device.is_mobile) {
      dispatch(closeDrawer());
    }

    if (!segment_id) {
      if (is_roundtrip) {
        search_params.set(RETURN_LCF, leg_card_id_class_and_fare);
      } else {
        search_params.set(OUTBOUND_LCF, leg_card_id_class_and_fare);
      }
    }

    if (has_seat_selection !== false && segment_id) {
      return redirectToSeatSelection(leg_card_id_class_and_fare, segment_id);
    }

    SeatSelectionView.setSelectedSeatsQS(search_params, selected_seats);

    return redirectToCart(trip_id, is_external);
  };

  const setJourneyId = useCallback(
    (journey_id: string | null | undefined) => {
      if (!journey_id) {
        search_params.delete(JOURNEY_ID_KEY);
      } else {
        search_params.set(JOURNEY_ID_KEY, journey_id);
      }
      history.replace({
        search: search_params.toString()
      });
    },
    [history, search_params]
  );

  return {
    removeCartId,
    addCartId,
    redirectToCart,
    redirectToTicketOptions,
    redirectToResults,
    redirectToSeatSelection,
    goBack,
    drawer_to_render,
    page_to_render,
    leg_card_id,
    cart_id,
    trip_id,
    onDrawerClose,
    onDrawerError,
    is_roundtrip,
    is_roundtrip_outbound_view,
    redirectToReturn: redirectToReturnResults,
    outbound_leg_card_id_class_and_fare,
    return_leg_card_id_class_and_fare,
    selected_seats,
    selectTrip,
    selectSeats,
    segment_id,
    utm_campaign,
    journey_id,
    setJourneyId
  };
};

const getPageClient = (search: string): Page => {
  const urlParams = new URLSearchParams(search);

  return getPage(
    urlParams.get(VIEW_KEY) as Page | null,
    urlParams.get(OPEN_DRAWER_KEY) as Drawer | null
  );
};

const getPage = (view_key: Page | null, drawer_key: Drawer | null): Page => {
  if (drawer_key) {
    switch (drawer_key) {
      case DRAWERS.SEAT_SELECTION:
        return PAGES.SEAT_SELECTION;
      case DRAWERS.TICKET_OPTIONS:
        return PAGES.TICKET_OPTIONS;
    }
  }

  if (view_key) {
    switch (view_key) {
      case PAGES.RESULTS:
        return PAGES.RESULTS;
      case PAGES.TICKET_OPTIONS:
        return PAGES.TICKET_OPTIONS;
      case PAGES.ASYNC_CART:
        return PAGES.ASYNC_CART;
      case PAGES.SEAT_SELECTION:
        return PAGES.SEAT_SELECTION;
    }
  }

  return PAGES.RESULTS;
};

export const getPageServer = (req: express.Request): Page => {
  return getPage(
    req.query[VIEW_KEY] as Page | null,
    req.query[OPEN_DRAWER_KEY] as Drawer | null
  );
};
